<template>
  <NotFoundPage v-if="isNotFound" :reason="isNotFound" />
  <div class="product-page heading" v-else>
    <ProductBreadcrumb :breadcrumb="breadcrumb"></ProductBreadcrumb>
    <div v-intersection="updateOverviewVisible" id="overview" ref="overview"></div>

    <div v-if="product" class="product-nav" :class="{ 'mobile-hidden': hideProductHeader }" ref="productNav">
      <ProductPageNav
        :isDualColumnEnabled="isDualColumnEnabled"
        :show-bundle="!!productBundleData"
        :product="product"
        :price="price"
        :current-navigation-category="currentNavigationCategory"
        :product-to-cart-format="productToCartFormat"
        @addToCart="trackAddToCart"
        :visible="visible"
        :isAddToCartVisibleOnPageNav="isAddToCartVisibleOnPageNav"
      />
    </div>
    <DualColumnLayout
      v-if="isDualColumnEnabled"
      :product="product"
      :loading="loading"
      :configurations="configurations"
      :price="price"
      :loadConfiguration="loadConfiguration"
      :lastImage="lastImage"
      :currentNavigationCategory="currentNavigationCategory"
      :trackAddToCart="trackAddToCart"
      :trackProductView="trackProductView"
      :productBundleData="productBundleData"
      :updateDetailsVisible="updateDetailsVisible"
      :updateOverviewVisible="updateOverviewVisible"
      :checkProductBuySectionATCInView="checkProductBuySectionATCInView"
      :disableAtcPageNav="disableAtcPageNav"
      :productToCartFormat="productToCartFormat"
    />

    <MainLayout
      v-if="!isDualColumnEnabled"
      :product="product"
      :loading="loading"
      :configurations="configurations"
      :price="price"
      :loadConfiguration="loadConfiguration"
      :lastImage="lastImage"
      :currentNavigationCategory="currentNavigationCategory"
      :trackProductView="trackProductView"
      :productBundleData="productBundleData"
      :updateDetailsVisible="updateDetailsVisible"
      :updateOverviewVisible="updateOverviewVisible"
      :checkProductBuySectionATCInView="checkProductBuySectionATCInView"
      :disableAtcPageNav="disableAtcPageNav"
    />

    <div v-if="ugcPixleeEnabled && showUgc && product" class="product-page-container product-ugc-wrapper">
      <ProductUGC
        :key="`product-ugc-${product.hashKey}`"
        :sku="product.skuNo"
        :show-new-heading="true"
        :similar-skus="similarSkus"
        @expandedVisible="onGalleryExpandedVisibleChange"
        @imagesUpdated="updateShowUgc"
      ></ProductUGC>
    </div>

    <div v-if="product" class="product-page-container product-reviews-container">
      <div id="reviews" ref="reviews" v-intersection="updateReviewsVisible">
        <NewProductReviews :product="product" :reviewUrl="reviewUrl" @reviewsEngaged="onProductReviewEngaged" />
      </div>

      <QAForm class="qa-form-container" />
    </div>

    <div
      v-if="productBundleData && product && product.isProductAvailableInGeo"
      id="bundles"
      class="product-page-container"
      ref="bundles"
      v-intersection="updateBundlesVisible"
    >
      <Divider margin-top="var(--space-10)" margin-bottom="var(--space-10)" />
      <ProductSet
        :key="`product-set-${product.hashKey}`"
        class="product-page-product-set"
        :set="productBundleData"
        title-on-top
        should-show-shop-all-sub-text
        @slideChange="onBundleEngaged"
        @productClick="onBundleEngaged"
        @addToCart="onBundleAddToCart"
      >
        <template v-slot:title>
          <h2 class="header-default light" v-if="productBundleData && productBundleData.title">{{ productBundleData.title }}</h2>
        </template>
      </ProductSet>
    </div>

    <Lazy>
      <div v-if="mounted && (showCrossSell || (product && product.related && product.related.length))" class="product-page-container">
        <Divider margin-top="var(--space-10)" margin-bottom="var(--space-10)" />
        <suspense>
          <CrossSell
            v-if="showCrossSell"
            :products="crossSellProducts"
            :products-per-view="4"
            :animate-cart-btn="true"
            @productAddedToCart="onCrossSellProductAdded"
          >
            <template v-slot:title>
              <h2 class="cross-sell-title">Often purchased with</h2>
            </template>
          </CrossSell>
          <RelatedProducts :product="product" v-else @carouselClicked="onCarouselClicked" />
        </suspense>
      </div>

      <div v-if="mounted && showCollections" class="product-page-container">
        <Divider margin-top="var(--space-10)" margin-bottom="var(--space-10)" />
        <Collections :product="product" @carouselClicked="onCarouselClicked" />
      </div>

      <div v-if="mounted && product" class="product-page-container" :key="`similar-products-${product.hashKey}`">
        <Divider v-if="similarProductsShown" margin-top="var(--space-10)" margin-bottom="var(--space-10)" />
        <SimilarProducts :product="product" @updated="onSimilarProductsUpdated" @carouselClicked="onCarouselClicked" />
      </div>

      <div class="product-footer-container">
        <ProductPageFooter class="product-page-container" />
      </div>
    </Lazy>

    <CanFavorite />

    <Snackbar
      v-if="mounted"
      :show="!!unsupportedBrowserMessage"
      @update:show="clearUnsupportedBrowserMessage"
      position="left"
      :message="unsupportedBrowserMessage"
    />
  </div>
</template>

<script>
import { Divider, Lazy, Snackbar } from '@/pixel/index';
import { computed, defineAsyncComponent, markRaw, onMounted, provide, reactive, ref, watch } from 'vue';
import useHead from '@/compositions/useHead';
import VueScrollTo from 'vue-scrollto';
import Tracking from '@/components/commons/Tracking.js';
import ProductFavorite from '@/components/product/ProductFavorite.vue';

import buildStructuredProduct from '@/components/meta/StructuredProduct.js';
import buildSocialMetaTags from '@/components/meta/Social.js';
import { scrollTop } from '@/components/commons/utils.js';
import ProductBreadcrumb from '@/components/breadcrumb/ProductBreadcrumb.vue';
import ProductBuySection from '@/components/product/ProductBuySection.vue';
import CanFavorite from '@/components/shared/CanFavorite.vue';
import ProductInformation from '@/components/product/ProductInformation.vue';
import NewProductReviews from '@/components/reviews/NewProductReviews.vue';
import IterableUtils from '@/components/commons/IterableUtils.js';
import UserService from '@/services/UserService';
import LocationService from '@/services/LocationService';

import useProductBundle from '@/compositions/useProductBundle';
import useCrossSell from '@/compositions/useCrossSell.js';
import useProduct from '@/compositions/useProduct';
import { useRouter, useRoute } from 'vue-router';
import ProductPageNav from '@/components/product/ProductPageNav.vue';
import useCommon from '@/compositions/useCommon';
import useLifecycleHook from '@/compositions/useLifecycleHook.js';
import Intersection from '@/components/commons/Intersection.js';
import ViewTime from '@/components/commons/ViewTime.js';
import { shouldAutoLaunchAR, loadARModel, openARViewer, buildUrl } from '@/components/commons/AugmentedReality.js';
import SurveyUtils from '@/components/commons/SurveyUtils.js';
import useStatusCode from '@/compositions/useStatusCode';
import useExperiment from '@/compositions/useExperiment';

export default {
  name: 'ProductPage',

  components: {
    Lazy,
    ProductFavorite,
    Divider,
    ProductInformation,
    Snackbar,
    ProductPageNav,
    NewProductReviews,
    ProductBuySection,
    ProductBreadcrumb,
    CanFavorite,
    QAForm: defineAsyncComponent(() => import('@/components/product/QAForm.vue')),
    RelatedProducts: defineAsyncComponent(() => import('@/components/product/RelatedProducts.vue')),
    CrossSell: defineAsyncComponent(() => import('@/components/commons/CrossSell.vue')),
    Collections: defineAsyncComponent(() => import('@/components/product/Collections.vue')),
    SimilarProducts: defineAsyncComponent(() => import('@/components/product/SimilarProducts.vue')),
    ProductUGC: defineAsyncComponent(() => import('@/components/ugc/ProductUGC.vue')),
    ProductPageFooter: defineAsyncComponent(() => import('@/components/product/Footer.vue')),
    ProductSet: defineAsyncComponent(() => import('@/components/sets/ProductSet.vue')),
    ProductGallery: defineAsyncComponent(() => import('@/components/product/ProductGallery.vue')),
    NotFoundPage: defineAsyncComponent(() => import('@/pages/Errors/NotFoundPage.vue')),
    DualColumnLayout: defineAsyncComponent(() => import('./DualColumnLayout.vue')),
    MainLayout: defineAsyncComponent(() => import('./MainLayout.vue')),
  },

  directives: {
    intersection: Intersection,
    viewTime: ViewTime,
  },

  data() {
    return {
      isVideoPlaying: false,
      showFavorite: true,
      hideProductHeader: false,
      similarProductsShown: true,
      notification: true,
      showHeadCart: false,
      hideTopInfo: false,
      isAddToCartVisibleOnPageNav: false,
    };
  },

  computed: {
    productToCartFormat() {
      return [
        {
          title: this.product.title,
          id: this.product ? this.product.hashKey : null,
          quantity: this.product ? this.product.packagedItemQuantity : 1,
        },
      ];
    },

    similarSkus() {
      return this.product && this.product.similarSku ? this.product.similarSku.map((similar) => similar.skuNo) : [];
    },
  },

  beforeRouteEnter(to, from, next) {
    next((vm) => {
      if (vm.route.query?.forceCurrencyId) {
        LocationService.setIsLocationManual(false);
        const query = Object.assign({}, vm.route.query);
        delete query.forceCurrencyId;

        try {
          // Server needs the forceCurrencyId query in case a redirect happens
          if (!import.meta.env.SSR) {
            vm.router.replace({ query });
          }
        } catch {}
      }
    });
  },

  async setup() {
    const isNotFound = ref('');
    const router = useRouter();
    const route = useRoute();
    const { mounted } = useLifecycleHook();
    const { loadProductBundleProductId } = useProductBundle();
    const { fetchCrossSellProducts, crossSellProducts } = useCrossSell();
    const {
      product,
      price,
      configurations,
      error,
      showUgc,
      load,
      loadConfiguration,
      lastImage,
      updateShowUgc,
      currentNavigationCategory,
      currentNavigationCategoryPath,
      showHeadCart,
      hideTopInfo,
      reviewUrl,
      trackAddToCart,
      trackProductView,
      loading,
    } = useProduct({ forcePair: route.query ? route.query.forcePair : false });
    const { redirectPermanent } = useStatusCode();
    const { getFeatureVariant } = useExperiment();
    const { initialized, ugcPixleeEnabled } = useCommon();

    const isDualColumnEnabled = computed(() => ['2-column', '2-column-expanded-gallery'].includes(getFeatureVariant('pdp-redesign-2022')));

    // const MIN_CROSS_SELL_COUNT = 3;

    const productBundleData = ref(null);
    const showCrossSell = ref(false);

    const loaded = ref(false);

    const visible = reactive({
      overview: false,
      details: false,
      reviews: false,
      bundles: false,
    });

    function updateOverviewVisible(intersecting) {
      visible.overview = intersecting;
    }

    function updateDetailsVisible(intersecting) {
      visible.details = intersecting;
    }

    function updateReviewsVisible(intersecting) {
      visible.reviews = intersecting;
    }

    function updateBundlesVisible(intersecting) {
      visible.bundles = intersecting;
    }

    const lastMedia = computed(() => {
      return product.value.media.length - 1;
    });

    const showCollections = computed(
      () => product.value && product.value.collection && product.value.collection.productThumbnail && product.value.collection.productCount > 1,
    );

    const metaTags = computed(() => {
      if (product && product.value) {
        return [
          {
            name: 'description',
            content: product.value.metaDescription,
          },
          ...buildSocialMetaTags({
            title: product.value.title,
            type: 'product',
            description: product.value.description,
            image: product.value.media && product.value.media.length ? { url: `${product.value.media[0].url}?fit=max&w={width}&q={q}` } : null,
            url: `${import.meta.env.VITE_STOREFRONT_BASE_URL}${
              router.resolve({ name: 'product', params: { id: product.value.hashKey, slug: product.value.slug } }).href
            }`,
            price: price ? price.finalFormatted : null,
            currency: price ? price.currency : null,
          }),
        ];
      }
      return [];
    });

    useHead(
      {
        title: computed(() => {
          if (product && product.value) {
            return product.value.metaTitle || `${product.value.title} | Article`;
          }
          return 'Article';
        }),
        meta: computed(() => metaTags.value),
        script: computed(() => [
          {
            type: 'application/ld+json',
            children: product && product.value ? JSON.stringify(buildStructuredProduct({ product: product.value, router })) : `{}`,
          },
        ]),
      },
      { appendTitleSuffix: false },
    );

    const breadcrumb = computed(() => {
      const nodes = [
        {
          title: 'Home',
          url: router.resolve({ name: 'home' }).href,
        },
      ];
      const category = currentNavigationCategory.value;
      if (product && product.value) {
        if (category && category.parentDisplayName) {
          nodes.push({
            title: category.parentDisplayName,
            url: router.resolve({
              name: 'browse',
              params: { id: category.parentId, slug: `${category.parentSlug}` },
            }).href,
          });
        }
        if (category) {
          nodes.push({
            title: category.displayName,
            url: router.resolve({
              name: 'browse',
              params: { id: category.id, slug: `${category.parentSlug}-${category.slug}` },
            }).href,
          });
        }
        nodes.push({
          title: product.value.title,
        });
      }
      return nodes;
    });

    function trackProductAddedToCart({ id, skuNo, title, productPrice }) {
      Tracking.track('CROSS_SELL_ENABLED_ADD_TO_CART', {
        product: JSON.stringify({
          id,
          skuNo,
          title,
          productPrice,
        }),
      });
    }

    function onCrossSellProductAdded(product) {
      scrollTop();
      trackProductAddedToCart(product);
    }

    async function updateCarousels(productId) {
      await fetchCrossSellProducts([productId]);
      try {
        productBundleData.value = await loadProductBundleProductId(productId, true);
      } catch (error) {
        productBundleData.value = {};
        Tracking.sentryCaptureError(error);
      }
    }

    const productMedias = computed(() => {
      return product.value && product.value.media
        ? product.value.media.map((media) => ({
            ...media,
            type: media.type || 'IMAGE',
            url: `${media.url}?fit=max&w={width}&q={q}`,
            thumbnailUrl: `${media.thumbnailUrl}?fit=max&w={width}&q={q}`,
            zoomUrl: `${media.url}?fit=max&w={width}&q=50`,
            product: product.value,
          }))
        : [];
    });

    watch(product, async () => {
      await updateCarousels(product.value.hashKey);
    });

    const unsupportedBrowserMessage = ref(null);

    function clearUnsupportedBrowserMessage() {
      unsupportedBrowserMessage.value = '';
    }

    function _calculateOffset(type) {
      console.log('isDualColumnEnabled.value', isDualColumnEnabled.value);
      if (window.matchMedia('(max-width: 970px)').matches) {
        if (type === 'details') return isDualColumnEnabled.value ? -40 : -120;
        if (type === 'reviews') return isDualColumnEnabled.value ? -16 : -90;
        if (type === 'bundles') return isDualColumnEnabled.value ? -20 : -90;

        return -150;
      }

      if (type === 'details') return -80;
      if (type === 'reviews') return isDualColumnEnabled.value ? 1 : -60;
      if (type === 'bundles') return -60;

      return -150;
    }

    function scrollToPosition(type, product) {
      Tracking.track('PDP_Click_Floating_Nav', { element: type, productId: product ? product.hashKey : null });
      const id = `#${type}`;
      const SCROLL_TIME = 1000;
      const offsetValue = _calculateOffset(type);
      VueScrollTo.scrollTo(id, SCROLL_TIME, { offset: offsetValue, container: 'body', lazy: false });
    }

    provide('scrollToPosition', scrollToPosition);

    onMounted(async () => {
      if (route.query.pr) {
        scrollToPosition('reviews');
      }

      trackProductView();

      const isLoggedIn = !!UserService.getEmail();

      if (isLoggedIn && product && product.value) {
        window.addEventListener('load', async () => {
          await IterableUtils.install();

          IterableUtils.account(import.meta.env.VITE_ITERABLE_API_KEY);
          IterableUtils.identify(UserService.getEmail(), {});
          IterableUtils.track('Product Viewed', {
            id: product.value.skuNo,
            name: product.value.title,
            category: currentNavigationCategoryPath.value,
            brand: 'Article',
            price: price && price?.value?.final ? Math.floor(price?.value?.final / 100) : null,
          });
        });
      }

      window.addEventListener('popstate', async () => {
        await load(route.params.id);
        trackProductView();
      });

      window.addEventListener('load', () => {
        loaded.value = true;
      });

      if (product && product.value && shouldAutoLaunchAR(route)) {
        const model = await loadARModel(product.value.hashKey);
        const modelUrl = buildUrl(model?.url, `${import.meta.env.VITE_STOREFRONT_BASE_URL}${route.path}`, product.value.slug);
        const success = openARViewer(modelUrl);

        if (!success) {
          unsupportedBrowserMessage.value = 'Please scan the QR code on an iOS device that supports augmented reality.';
          setTimeout(clearUnsupportedBrowserMessage, 4000);
        } else {
          Tracking.track('Open AR from QR Code');
          setTimeout(() => {
            SurveyUtils.install();
          }, 2000);
        }
      }
    });

    await load(route.params.id);
    await updateCarousels(route.params.id);

    if (import.meta.env.SSR) {
      if (error.value) {
        throw new Error(error || `Failed to load product with id ${route.params.id}`);
      } else if (product.value && product.value.hashKey && route.query.preview === 'true') {
        // Allow previewing product page without redirect
      } else if (product.value && product.value.activeVersionHashKey && product.value.activeVersionHashKey !== product.value.hashKey) {
        // Redirect inactive products if an active version is available

        redirectPermanent(
          router.resolve({
            name: 'product',
            params: {
              id: product.value.activeVersionHashKey,
              slug: product.value.slug,
            },
            query: route.query.forcePair ? { forcePair: route.query.forcePair } : {},
          }).href,
          `Product ${product.value.hashKey} has newer active product version ${product.value.activeVersionHashKey}.`,
        );
      } else if (product.value && product.value.status === 'DISCONTINUED' && currentNavigationCategory.value && currentNavigationCategory.value.id) {
        const redirectCategory = currentNavigationCategory.value;
        redirectPermanent(
          router.resolve({
            name: 'browse',
            params: {
              id: redirectCategory.id,
              slug: `${redirectCategory.parentSlug ? redirectCategory.parentSlug : ''}-${redirectCategory.slug}`,
            },
            query: {
              productId: product.value.hashKey,
            },
          }).href,
          `Product ${product.value.hashKey} is discontinued so redirecting to category ${redirectCategory.id}.`,
        );
        return;
      } else if (product.value && route.params.slug !== product.value.slug) {
        redirectPermanent(
          router.resolve({
            name: 'product',
            params: { id: product.value.hashKey, slug: product.value.slug },
            query: { forceCurrencyId: route.query.forceCurrencyId, ctpreview: route.query.ctpreview },
          }).href,
          `The provided product slug (${route.params.slug}) is different to the expected product slug (${product.value.slug}).`,
        );
        return;
      }
    } else {
      if (product && product.value && route.params.slug !== product.value.slug) {
        const url = router.resolve({
          name: 'product',
          params: { id: product.value.hashKey, slug: product.value.slug },
        }).href;
        window.history.replaceState(window.history.state, '', url);
      }

      try {
        productBundleData.value = await loadProductBundleProductId(route.params.id, true);
      } catch (error) {
        Tracking.sentryCaptureError(error);
      }
    }

    if (!product.value || !product.value.hashKey || (!product.value.active && route.query.preview !== 'true')) {
      isNotFound.value = 'Product not found';
    }

    return {
      isDualColumnEnabled,
      load,
      loadConfiguration,
      configurations,
      product,
      price,
      lastImage,
      mounted,
      error,
      crossSellProducts,
      onCrossSellProductAdded,
      showCrossSell,
      productBundleData,
      initialized,
      ugcPixleeEnabled,

      showUgc,
      updateShowUgc: (images) => updateShowUgc(!!images && !!images.length),
      currentNavigationCategory,
      currentNavigationCategoryPath,

      breadcrumb,
      showHeadCart,
      hideTopInfo,
      reviewUrl,
      trackAddToCart,
      trackProductView,
      loaded,
      loading,
      visible,
      updateOverviewVisible,
      updateDetailsVisible,
      updateReviewsVisible,
      updateBundlesVisible,
      productMedias,
      lastMedia,
      router,
      route,

      showCollections,

      ProductContentfulSection: markRaw(defineAsyncComponent(() => import('@/components/product/ProductContentfulSection.vue'))),
      unsupportedBrowserMessage,
      clearUnsupportedBrowserMessage,
      isNotFound,
    };
  },
  methods: {
    checkProductBuySectionATCInView(el) {
      if (!el || !this.$refs.productNav) {
        return;
      }
      const elementRect = el.getBoundingClientRect();
      const productNavRect = this.$refs.productNav.getBoundingClientRect();
      if (elementRect.bottom < productNavRect.bottom && !this.isAddToCartVisibleOnPageNav) {
        this.isAddToCartVisibleOnPageNav = !this.isAddToCartVisibleOnPageNav;
      }
      if (elementRect.bottom > productNavRect.bottom && this.isAddToCartVisibleOnPageNav) {
        this.isAddToCartVisibleOnPageNav = !this.isAddToCartVisibleOnPageNav;
      }
    },
    onGalleryExpandedVisibleChange(visible) {
      this.hideProductHeader = visible;
    },
    disableAtcPageNav() {
      this.isAddToCartVisibleOnPageNav = false;
    },

    onSimilarProductsUpdated(products) {
      this.similarProductsShown = products ? products.length : 0;
    },

    onBundleEngaged() {
      Tracking.track('PDP_BUNDLE_INTERACTION');
    },

    onBundleAddToCart() {
      Tracking.track('PDP_BUNDLE_ADD_TO_CART');
    },

    onProductReviewEngaged() {
      Tracking.track('PDP_BUNDLE_REVIEW_ENGAGEMENT', {
        containBundle: !!this.productBundleData,
      });
    },

    onCarouselClicked(prop) {
      Tracking.track('Collection-interaction', {
        id: prop.id.clicked,
        Carousel: prop.carousel,
      });
    },
  },
};
</script>

<style lang="postcss" scoped>
@import '@/pixel/style/variables';
@import '@/pixel/style/mixins';

.round {
  right: var(--space-4);
}

.product-nav {
  position: sticky;
  position: -webkit-sticky;
  top: 0;
  left: 0;
  overflow: hidden;
  z-index: 11;
  background: #ffffff;
  margin-top: 8px;

  @media (min-width: $tablet-breakpoint) {
    margin-top: v-bind(isDualColumnEnabled ? '0px': '8px');
  }
}

.product-page-container {
  @media (max-width: calc($desktop-breakpoint - 1px)) {
    .gallery {
      padding: 0;
      width: 100%;
    }

    .mobile-only {
      display: none;
    }
  }

  @media (min-width: $large-desktop-breakpoint) {
    @mixin container 2560px;
  }

  position: relative;

  .product-carousel-title {
    font-size: 1.5em;
  }

  .row {
    &.large {
      margin-bottom: 20px;
    }
  }
  .badge-fade-enter-from,
  .badge-fade-leave-to {
    opacity: 0;
  }

  .badge-fade-enter-active,
  .badge-fade-leave-active {
    transition: opacity 100ms ease-out;
  }
  &.similar-products-carousel {
    margin-top: 32px;
    margin-bottom: 24px;
    .favorite {
      right: -10px;
      background: transparent;
    }
  }

  .cross-sell-title {
    font-family: var(--font-light);
  }
}

.product-page {
  width: 100%;

  .reserve-breadcrumb {
    height: 24px;
    width: 100%;
  }

  .mobile-hidden {
    visibility: hidden;
  }

  .product-page-badge {
    z-index: 3;
    top: 20px;
    left: 20px;
  }

  .delivery-row {
    position: relative;
    margin-top: 12px;
  }

  .qa-form-container {
    margin-top: var(--space-10);
  }

  .delivery-message-container {
    .delivery-message-bottom {
      margin-top: 0px;
      visibility: hidden;
    }

    @media (min-width: $tablet-breakpoint) {
      background: var(--background-color);
    }
  }

  .product-ugc-wrapper {
    overflow: hidden;
  }

  .product-gallery-fade-enter-active,
  .product-gallery-fade-leave-active {
    transition: opacity 300ms ease-in-out;
  }

  .product-gallery-fade-enter-from,
  .product-gallery-fade-leave-to {
    opacity: 0;
  }
  .product-page-product-set {
    /* purgecss ignore */
    ::v-deep(.product-set-image) {
      @media (max-width: calc($tablet-breakpoint - 1px)) {
        margin: auto;
      }
    }
  }
}
</style>
